import type { MetricOutputDisplaySource } from "@/types/RunMetricOutput"

import { parseOffsets } from "./parseOffsets"

export interface Segment {
  start: number
  end: number
}

/** Default tolerance (seconds) used when comparing metric vs human audio segments. */
export const DEFAULT_TOLERANCE_SECONDS = 0.1

/** Minimum duration (seconds) for a segment when end is missing or too close to start. */
const MIN_SEGMENT_DURATION = 0.05

/** Normalized-time tolerance used when matching metric and human segments. */
const MATCH_TOLERANCE = 0.05

// ---------------------------------------------------------------------------
// Segment extraction helpers
// ---------------------------------------------------------------------------

interface MetricSegmentSource {
  getAudioRegions: (
    output: MetricOutputDisplaySource | undefined,
  ) => Array<{ start: number; end?: number }>
}

interface HumanSegmentAnnotation {
  ground_truth_subvalues_by_timestamp?: unknown
  groundTruthSubvaluesByTimestamp?: unknown
}

export function getMetricSegments(
  metricRecord: MetricSegmentSource | null | undefined,
  fullMetricOutput: MetricOutputDisplaySource | null | undefined,
): Segment[] {
  if (!metricRecord || !fullMetricOutput) {
    return []
  }
  try {
    const regions = metricRecord.getAudioRegions(fullMetricOutput)
    return regions.map((r) => ({
      start: r.start,
      end: Math.max(
        r.end ?? r.start + MIN_SEGMENT_DURATION,
        r.start + MIN_SEGMENT_DURATION,
      ),
    }))
  } catch {
    return []
  }
}

export function getHumanSegments(
  annotation: HumanSegmentAnnotation | null | undefined,
): Segment[] {
  if (!annotation) {
    return []
  }
  const raw =
    annotation.ground_truth_subvalues_by_timestamp ??
    annotation.groundTruthSubvaluesByTimestamp
  if (raw == null) {
    return []
  }
  const arr: Array<{ start_offset: number; end_offset?: number }> =
    parseOffsets(raw)
  return arr.map((s) => ({
    start: s.start_offset,
    end: Math.max(
      s.end_offset ?? s.start_offset + MIN_SEGMENT_DURATION,
      s.start_offset + MIN_SEGMENT_DURATION,
    ),
  }))
}

// ---------------------------------------------------------------------------
// Agreement logic
// ---------------------------------------------------------------------------

function intervalOverlap(
  aStart: number,
  aEnd: number,
  bStart: number,
  bEnd: number,
): number {
  return Math.max(0, Math.min(aEnd, bEnd) - Math.max(aStart, bStart))
}

const MARK_DURATION_EPS = 0.01

function timelineDuration(
  metricSegments: Segment[],
  humanSegments: Segment[],
): number {
  const allEnds = [
    ...metricSegments.map((s) => s.end),
    ...humanSegments.map((s) => s.end),
  ]
  return allEnds.length > 0 ? Math.max(...allEnds, 1) : 1
}

function toNormalized(seg: Segment, duration: number): Segment {
  return {
    start: seg.start / duration,
    end: Math.min(seg.end / duration, 1),
  }
}

function segmentsOverlapOrContain(
  a: Segment,
  b: Segment,
  tolerance = 0,
): boolean {
  const aDur = a.end - a.start
  const bDur = b.end - b.start
  const overlap = intervalOverlap(a.start, a.end, b.start, b.end)
  if (overlap > tolerance) {
    return true
  }
  if (aDur <= MARK_DURATION_EPS && aDur >= 0) {
    const t = a.start
    if (t >= b.start - tolerance && t <= b.end + tolerance) {
      return true
    }
  }
  if (bDur <= MARK_DURATION_EPS && bDur >= 0) {
    const t = b.start
    if (t >= a.start - tolerance && t <= a.end + tolerance) {
      return true
    }
  }
  return false
}

export function segmentsAgree(
  metricSegments: Segment[],
  humanSegments: Segment[],
): boolean {
  if (metricSegments.length === 0 && humanSegments.length === 0) {
    return true
  }
  if (metricSegments.length === 0 || humanSegments.length === 0) {
    return false
  }
  const duration = timelineDuration(metricSegments, humanSegments)
  const normM = metricSegments.map((s) => toNormalized(s, duration))
  const normH = humanSegments.map((s) => toNormalized(s, duration))
  const tol = MATCH_TOLERANCE
  const matchedMetric = new Set<number>()
  const matchedHuman = new Set<number>()
  for (let i = 0; i < normM.length; i++) {
    const m = normM[i]
    for (let j = 0; j < normH.length; j++) {
      if (matchedHuman.has(j)) {
        continue
      }
      const h = normH[j]
      if (segmentsOverlapOrContain(m, h, tol)) {
        matchedMetric.add(i)
        matchedHuman.add(j)
        break
      }
    }
  }
  return (
    normM.length - matchedMetric.size === 0 &&
    normH.length - matchedHuman.size === 0
  )
}

// ---------------------------------------------------------------------------
// Per-segment tolerance agreement (absolute seconds)
// ---------------------------------------------------------------------------

/**
 * Compute FP + FN error in seconds for a matched pair of segments.
 * FP = time in metric seg not covered by human seg
 * FN = time in human seg not covered by metric seg
 */
function pairErrorSeconds(metric: Segment, human: Segment): number {
  const overlap = intervalOverlap(
    metric.start,
    metric.end,
    human.start,
    human.end,
  )
  const metricDur = metric.end - metric.start
  const humanDur = human.end - human.start
  const fp = metricDur - overlap
  const fn = humanDur - overlap
  return fp + fn
}

/**
 * Check if metric and human segments agree within an absolute per-segment
 * tolerance in seconds. Each metric segment is matched to the best-overlapping
 * human segment. If any matched pair has FP + FN > toleranceSeconds, or any
 * segment is unmatched and its duration > toleranceSeconds, returns false.
 */
export function segmentsAgreeWithTolerance(
  metricSegments: Segment[],
  humanSegments: Segment[],
  toleranceSeconds: number,
): boolean {
  if (metricSegments.length === 0 && humanSegments.length === 0) {
    return true
  }
  if (metricSegments.length === 0 || humanSegments.length === 0) {
    return false
  }

  // Greedy matching: pair each metric segment with its best-overlapping human
  // segment (most overlap first).
  const matchedHuman = new Set<number>()
  const metricMatches = new Map<number, number>() // metric idx → human idx

  for (let i = 0; i < metricSegments.length; i++) {
    const m = metricSegments[i]
    let bestJ = -1
    let bestOverlap = 0
    for (let j = 0; j < humanSegments.length; j++) {
      if (matchedHuman.has(j)) {
        continue
      }
      const ov = intervalOverlap(
        m.start,
        m.end,
        humanSegments[j].start,
        humanSegments[j].end,
      )
      if (ov > bestOverlap) {
        bestOverlap = ov
        bestJ = j
      }
    }
    if (bestJ >= 0) {
      metricMatches.set(i, bestJ)
      matchedHuman.add(bestJ)
    }
  }

  // Check matched pairs
  for (const [mi, hi] of metricMatches) {
    if (
      pairErrorSeconds(metricSegments[mi], humanSegments[hi]) > toleranceSeconds
    ) {
      return false
    }
  }

  // Unmatched metric segments (pure false positives)
  for (let i = 0; i < metricSegments.length; i++) {
    if (!metricMatches.has(i)) {
      const dur = metricSegments[i].end - metricSegments[i].start
      if (dur > toleranceSeconds) {
        return false
      }
    }
  }

  // Unmatched human segments (pure false negatives)
  for (let j = 0; j < humanSegments.length; j++) {
    if (!matchedHuman.has(j)) {
      const dur = humanSegments[j].end - humanSegments[j].start
      if (dur > toleranceSeconds) {
        return false
      }
    }
  }

  return true
}
